import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from "react";
import { Sidebar } from "@/components/sidebar/sidebar";
import "./links-screen.scss";
// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
const API_BASE = "https://api.treehouse.putty.inc";
async function fetchLinks() {
    const response = await fetch(`${API_BASE}/v1/links`, {
        credentials: "include"
    });
    if (!response.ok)
        throw new Error("Failed to fetch links");
    const data = await response.json();
    return data.links;
}
async function createLink(path, redirect_url) {
    const response = await fetch(`${API_BASE}/v1/links`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ path, redirect_url })
    });
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to create link");
    }
    const data = await response.json();
    return data.link;
}
async function deleteLink(path) {
    const response = await fetch(`${API_BASE}/v1/links/${path}`, {
        method: "DELETE",
        credentials: "include"
    });
    if (!response.ok)
        throw new Error("Failed to delete link");
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function formatDate(dateStr) {
    if (!dateStr)
        return "Never";
    const date = new Date(dateStr + "Z"); // D1 stores as UTC without Z
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const ONE_WEEK_DAYS = 7;
    // Within a week: show relative time
    if (diffDays < ONE_WEEK_DAYS) {
        if (diffSeconds < 60)
            return "Just now";
        if (diffMinutes < 60)
            return `${diffMinutes}m ago`;
        if (diffHours < 24)
            return `${diffHours}h ago`;
        return `${diffDays}d ago`;
    }
    // Older than a week: show date only
    return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric"
    });
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function LinksScreen() {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Form state
    const [newPath, setNewPath] = useState("");
    const [newUrl, setNewUrl] = useState("");
    const [creating, setCreating] = useState(false);
    const [formError, setFormError] = useState(null);
    const loadLinks = useCallback(async () => {
        try {
            setError(null);
            const data = await fetchLinks();
            setLinks(data);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : "Failed to load links");
        }
        finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        document.title = "Treehouse · Links";
    }, []);
    useEffect(() => {
        loadLinks();
    }, [loadLinks]);
    const handleCreate = async (e) => {
        e.preventDefault();
        if (!newPath.trim() || !newUrl.trim())
            return;
        setCreating(true);
        setFormError(null);
        try {
            const link = await createLink(newPath.trim(), newUrl.trim());
            setLinks((prev) => [link, ...prev]);
            setNewPath("");
            setNewUrl("");
        }
        catch (e) {
            setFormError(e instanceof Error ? e.message : "Failed to create link");
        }
        finally {
            setCreating(false);
        }
    };
    const handleDelete = async (path) => {
        if (!confirm(`Delete go.putty.inc/${path}?`))
            return;
        try {
            await deleteLink(path);
            setLinks((prev) => prev.filter((l) => l.path !== path));
        }
        catch (e) {
            alert(e instanceof Error ? e.message : "Failed to delete");
        }
    };
    return (_jsxs("div", { className: "th_layout", children: [_jsx(Sidebar, {}), _jsx("main", { className: "th_main", children: _jsxs("div", { className: "th_content", children: [_jsx("h1", { className: "th_heading--xl", children: "Short Links" }), _jsxs("form", { className: "links__form", onSubmit: handleCreate, children: [_jsxs("div", { className: "links__form-row", children: [_jsxs("div", { className: "links__form-field", children: [_jsx("label", { htmlFor: "path", children: "Path" }), _jsxs("div", { className: "links__input-prefix", children: [_jsx("span", { children: "go/" }), _jsx("input", { id: "path", type: "text", value: newPath, onChange: (e) => setNewPath(e.target.value), onKeyDown: (e) => e.key === "Escape" && e.currentTarget.blur(), placeholder: "my-link", disabled: creating, autoFocus: true })] })] }), _jsxs("div", { className: "links__form-field links__form-field--url", children: [_jsx("label", { htmlFor: "url", children: "Redirect URL" }), _jsx("input", { id: "url", type: "url", value: newUrl, onChange: (e) => setNewUrl(e.target.value), onKeyDown: (e) => e.key === "Escape" && e.currentTarget.blur(), placeholder: "https://example.com/page", disabled: creating })] }), _jsx("button", { type: "submit", className: "th_button th_button--primary", disabled: creating || !newPath.trim() || !newUrl.trim(), children: creating ? "Creating..." : "Create" })] }), formError && _jsx("div", { className: "links__form-error", children: formError })] }), loading ? (_jsx("div", { className: "links__loading", children: "Loading..." })) : error ? (_jsx("div", { className: "links__error", children: error })) : links.length === 0 ? (_jsx("div", { className: "links__empty", children: "No short links yet. Create one above!" })) : (_jsxs("table", { className: "links__table", children: [_jsxs("colgroup", { children: [_jsx("col", { style: { width: "20%" } }), _jsx("col", { style: { width: "auto" } }), _jsx("col", { style: { width: "70px" } }), _jsx("col", { style: { width: "160px" } }), _jsx("col", { style: { width: "160px" } }), _jsx("col", { style: { width: "40px" } })] }), _jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { children: "Short Link" }), _jsx("th", { children: "Redirect URL" }), _jsx("th", { children: "Visits" }), _jsx("th", { children: "Last Used" }), _jsx("th", { children: "Created" }), _jsx("th", {})] }) }), _jsx("tbody", { children: links.map((link) => (_jsxs("tr", { children: [_jsx("td", { children: _jsxs("a", { href: `https://go.putty.inc/${link.path}`, target: "_blank", rel: "noopener noreferrer", className: "links__short-url", children: ["go/", link.path] }) }), _jsx("td", { className: "links__redirect-url", children: _jsx("a", { href: link.redirect_url, target: "_blank", rel: "noopener noreferrer", children: link.redirect_url }) }), _jsx("td", { className: "links__visits", children: link.visit_count }), _jsx("td", { className: "links__date", children: formatDate(link.last_used_at) }), _jsx("td", { className: "links__date", children: formatDate(link.created_at) }), _jsx("td", { children: _jsx("button", { className: "links__delete", onClick: () => handleDelete(link.path), title: "Delete", children: "\u00D7" }) })] }, link.path))) })] }))] }) })] }));
}
